import { HttpClient } from './service'

// Type definitions for budget settings entities
export interface IInternalPosition {
  id: number
  key: string
  position_name: string  // Standardized field from API
  category: number  // category ID
  department: number  // department ID
  department_name: string  // read-only field from serializer
  unit: number  // unit ID
  unit_name: string  // read-only field from serializer
  hour_data: Array<{
    id: string;
    hours: number;
    position_id: number;
    department_id: number;
    position_name?: string;
    department_name?: string;
    position_shortcut: string;
    department_shortcut: string;
  }>  // List format hour_data
  total_hours: number | string  // Can be string from API (e.g., "180.00")
  is_active: boolean
  created_at: string
  updated_at: string
}

// Grouped response format for internal positions (grouped by department)
export interface IDepartmentGroup {
  id: number
  name: string
  positions: IInternalPosition[]
}

export interface IExternalPosition {
  id: number
  position: string
  supplier: string | null
  unit: string
  unitPrice: number
}

export interface IUnit {
  id: number
  name: string
  relationship: 'related' | 'unrelated' | 'unique' | 'weekly' | 'bi-weekly' | 'monthly' | 'yearly'
  is_read_only: boolean
  created_at: string
  updated_at: string
}

export interface IUnitBudgetUsage {
  [unitId: string]: string[]
}

export interface IClient {
  id: number
  name: string
  contact: string
}

export interface ISupplier {
  id: number
  name: string
  contact: string
}

export interface IBudgetSettingsCategory {
  id: number
  name: string
  display_order: number
  created_at: string
  updated_at: string
  created_by: number | null
  created_by_name: string | null
  updated_by: number | null
  updated_by_name: string | null
  department_roles: IInternalPosition[]
}

// Response types
export interface IBudgetSettingsResponse<T> {
  results: T[]
  count: number
  links?: {
    next: string | null
    previous: string | null
  }
  page_size?: number
  current_page?: number
  total_pages?: number
}

class BudgetSettingsApi {
  // Internal Positions API
  async getInternalPositions(filters?: any): Promise<IInternalPosition[]> {
    return HttpClient.get<IInternalPosition[], any>('/api/budget-module/settings/internal-positions/', filters)
  }
  
  async getInternalPositionsGrouped(): Promise<IDepartmentGroup[]> {
    return HttpClient.get<IDepartmentGroup[], any>('/api/budget-module/settings/internal-positions/', { group_by_department: 'true' })
  }

  async createInternalPosition(data: Partial<IInternalPosition>): Promise<IInternalPosition> {
    return HttpClient.post<IInternalPosition, Partial<IInternalPosition>>('/api/budget-module/settings/internal-positions/', data)
  }

  async updateInternalPosition(id: number, data: Partial<IInternalPosition>): Promise<IInternalPosition> {
    return HttpClient.patch<IInternalPosition, Partial<IInternalPosition>>('/api/budget-module/settings/internal-positions/', id, data)
  }

  async bulkUpdateInternalPositions(updates: Array<{ id: number, data: Partial<IInternalPosition> }>): Promise<IInternalPosition[]> {
    // Execute PATCH requests SEQUENTIALLY to avoid race conditions with JSON field updates
    // Multiple concurrent updates to the same BudgetProject.budget_positions can overwrite each other
    console.log(`🚀 Starting ${updates.length} sequential PATCH requests to avoid race conditions...`)
    
    const results: IInternalPosition[] = []
    
    for (let index = 0; index < updates.length; index++) {
      const update = updates[index]
      console.log(`📤 PATCH ${index + 1}/${updates.length}: Position ${update.id}`, update.data)
      
      try {
        const response = await HttpClient.patch<IInternalPosition, Partial<IInternalPosition>>('/api/budget-module/settings/internal-positions/', update.id, update.data)
        console.log(`✅ PATCH ${index + 1}/${updates.length} completed: Position ${update.id}`)
        results.push(response)
      } catch (error) {
        console.error(`❌ PATCH ${index + 1}/${updates.length} failed: Position ${update.id}`, error)
        throw error
      }
    }
    
    console.log(`🎉 All ${updates.length} sequential PATCH requests completed successfully`)
    return results
  }

  async deleteInternalPosition(id: number): Promise<void> {
    return HttpClient.delete<void, number>('/api/budget-module/settings/internal-positions/', id)
  }

  // External Positions API
  async getExternalPositions(filters?: any): Promise<IExternalPosition[]> {
    return HttpClient.get<IExternalPosition[], any>('/api/budget-module/settings/external-positions/', filters)
  }

  async createExternalPosition(data: Partial<IExternalPosition>): Promise<IExternalPosition> {
    return HttpClient.post<IExternalPosition, Partial<IExternalPosition>>('/api/budget-module/settings/external-positions/', data)
  }

  async updateExternalPosition(id: number, data: Partial<IExternalPosition>): Promise<IExternalPosition> {
    return HttpClient.patch<IExternalPosition, Partial<IExternalPosition>>('/api/budget-module/settings/external-positions/', id, data)
  }

  async deleteExternalPosition(id: number): Promise<void> {
    return HttpClient.delete<void, number>('/api/budget-module/settings/external-positions/', id)
  }

  // Units API
  async getUnits(filters?: any): Promise<IUnit[]> {
    return HttpClient.get<IUnit[], any>('/api/budget-module/settings/units/', filters)
  }

  async createUnit(data: Partial<IUnit>): Promise<IUnit> {
    return HttpClient.post<IUnit, Partial<IUnit>>('/api/budget-module/settings/units/', data)
  }

  async updateUnit(id: number, data: Partial<IUnit>): Promise<IUnit> {
    return HttpClient.put<IUnit, Partial<IUnit>>('/api/budget-module/settings/units/', id, data)
  }

  async deleteUnit(id: number): Promise<void> {
    return HttpClient.delete<void, number>('/api/budget-module/settings/units/', id)
  }

  /**
   * Fetch budget project usage for all units
   * @returns Mapping of unit ID to list of budget project names
   */
  async getUnitBudgetUsage(): Promise<IUnitBudgetUsage> {
    return HttpClient.get<IUnitBudgetUsage, void>('/api/budget-module/settings/units/budget-usage/')
  }

  // Clients API
  async getClients(filters?: any): Promise<IClient[]> {
    return HttpClient.get<IClient[], any>('/api/budget-module/settings/clients/all/', filters)
  }

  async createClient(data: Partial<IClient>): Promise<IClient> {
    return HttpClient.post<IClient, Partial<IClient>>('/api/budget-module/settings/clients/', data)
  }

  async updateClient(id: number, data: Partial<IClient>): Promise<IClient> {
    return HttpClient.patch<IClient, Partial<IClient>>('/api/budget-module/settings/clients/', id, data)
  }

  async deleteClient(id: number): Promise<void> {
    return HttpClient.delete<void, number>('/api/budget-module/settings/clients/', id)
  }

  // Suppliers API
  async getSuppliers(filters?: any): Promise<ISupplier[]> {
    return HttpClient.get<ISupplier[], any>('/api/budget-module/settings/suppliers/all/', filters)
  }

  async createSupplier(data: Partial<ISupplier>): Promise<ISupplier> {
    return HttpClient.post<ISupplier, Partial<ISupplier>>('/api/budget-module/settings/suppliers/', data)
  }

  async updateSupplier(id: number, data: Partial<ISupplier>): Promise<ISupplier> {
    return HttpClient.put<ISupplier, Partial<ISupplier>>('/api/budget-module/settings/suppliers/', id, data)
  }

  async deleteSupplier(id: number): Promise<void> {
    return HttpClient.delete<void, number>('/api/budget-module/settings/suppliers/', id)
  }

  // Categories API
  async getCategories(filters?: any): Promise<IBudgetSettingsCategory[]> {
    return HttpClient.get<IBudgetSettingsCategory[], any>('/api/budget-module/settings/categories/', filters)
  }

  async createCategory(data: Partial<IBudgetSettingsCategory>): Promise<IBudgetSettingsCategory> {
    return HttpClient.post<IBudgetSettingsCategory, Partial<IBudgetSettingsCategory>>('/api/budget-module/settings/categories/', data)
  }

  async updateCategory(id: number, data: Partial<IBudgetSettingsCategory>): Promise<IBudgetSettingsCategory> {
    return HttpClient.patch<IBudgetSettingsCategory, Partial<IBudgetSettingsCategory>>('/api/budget-module/settings/categories/', id, data)
  }

  async deleteCategory(id: number): Promise<void> {
    return HttpClient.delete<void, number>('/api/budget-module/settings/categories/', id)
  }
}

export const budgetSettingsApi = new BudgetSettingsApi() 